import React, { useState, ReactNode } from 'react';
import { Input, Select } from 'antd';
import classNames from 'classnames';
import _orderBy from 'lodash/orderBy';
import _isNil from 'lodash/isNil';
import _isFunction from 'lodash/isFunction';

import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { EarlyEtaStrategy } from '@bringg/types';
import PriorityConfiguration, {
	FleetsType,
	Translations as PriorityTranslations
} from './priority-configuration/priority-configuration';
import TeamsSection, { SelectedTeams, Team, Translations as TeamTranslations } from './teams-section/teams-section';
import FormButtons, { Translations as FormButtonsTranslations } from './form-buttons/form-buttons';
import { FallbackMethod, useFallback } from './priority-configuration/fallback/use-fallback';
import usePrevious from '../../../custom-hooks/use-previous/use-previous';
import { TEST_IDS } from '../test-ids';
import { ReadOnlyMessage } from '../read-only-message';

interface Props {
	translations: Translations;
	allFleets: FleetsType;
	allTeams: Team[];
	disabledTeamIds?: Set<string>;
	conflictRulesByTeamId: Map<string, string[]>;
	ruleConfig: RuleConfig;
	onRemove?: (id: string) => void;
	onApply: (ruleConfig: RuleConfig) => void;
	onCancel: () => void;
	readOnly?: boolean;
	validationByRuleType?: Partial<Record<RuleType, ReactNode>>;
}

export interface RuleConfig {
	id: string;
	name: string;
	isMerchantLevel: boolean;
	selectedTeams: SelectedTeams;
	ruleType: RuleType;
	priorityConfiguration?: PriorityConfigurationType;
	earlyEtaStrategy?: EarlyEtaStrategy;
}

export interface PriorityConfigurationType {
	selectedFleetIds: string[];
	isFallbackMethodEnabled: boolean;
	fallbackMethod?: FallbackMethod;
}

export interface Translations {
	selectAll: string;
	priority: string;
	fast: string;
	cheap: string;
	name: string;
	type: string;
	editRule: string;
	fallbackHeader: string;
	fallbackTooltip: string;
	teamSectionTooltip: string;
	setToSpecificTeams: string;
	noSelectedFleets: string;
	noMoreFleetsToAdd: string;
	addFleet: string;
	showSelected: string;
	teamSectionDescription: string;
	searchPlaceholder: string;
	teamSectionTitle: string;
	disabledTeamTranslation: string;
	cancel: string;
	apply: string;
	remove: string;
	teamsAssignedToRules: string;
	readOnlyModeMessage?: string;
	internalFleetName?: string;
}

export enum RuleType {
	fast = 'fast',
	cheap = 'cheap',
	priority = 'priority',
	ratio = 'ratio'
}

const RuleForm: React.FC<Props> = ({
	translations,
	ruleConfig,
	allFleets,
	allTeams,
	disabledTeamIds,
	conflictRulesByTeamId,
	readOnly,
	onRemove,
	onApply,
	onCancel,
	validationByRuleType = {}
}) => {
	const [selectedTeams, setSelectedTeams] = useState(ruleConfig.selectedTeams);
	const [ruleType, setRuleType] = useState(ruleConfig.ruleType);
	const [isTeamLevel, setTeamLevel] = useState(ruleConfig.isMerchantLevel);
	const [name, setName] = useState(ruleConfig.name);
	const [selectedFleetIds, setSelectedFleetIds] = useState(
		ruleConfig.priorityConfiguration?.selectedFleetIds || getDefaultFleets(allFleets)
	);
	const { fallbackMethod, isFallbackMethodEnabled, setIsFallbackMethodEnabled, setFallbackMethod } = useFallback({
		initialEnabled: ruleConfig.priorityConfiguration?.isFallbackMethodEnabled ?? false,
		initialMethod: ruleConfig.priorityConfiguration?.fallbackMethod || FallbackMethod.FAST
	});
	const prevRuleType = usePrevious(ruleType);

	const _onApply = () => {
		const config: RuleConfig = {
			id: ruleConfig.id,
			name,
			isMerchantLevel: isTeamLevel,
			ruleType,
			selectedTeams,
			...(ruleType === RuleType.priority && {
				priorityConfiguration: {
					...(isFallbackMethodEnabled && { fallbackMethod }),
					isFallbackMethodEnabled,
					selectedFleetIds
				}
			})
		};

		onApply(config);
	};

	const _onRemove = () => {
		onRemove(ruleConfig.id);
	};

	const teamsSectionTranslations: TeamTranslations = {
		showSelected: translations.showSelected,
		description: translations.teamSectionDescription,
		searchPlaceholder: translations.searchPlaceholder,
		title: translations.teamSectionTitle,
		setToSpecificTeams: translations.setToSpecificTeams,
		teamSectionTooltip: translations.teamSectionTooltip,
		selectAll: translations.selectAll,
		disabledTeamTranslation: translations.disabledTeamTranslation,
		teamsAssignedToRules: translations.teamsAssignedToRules
	};

	const priorityTranslations: PriorityTranslations = {
		addFleet: translations.addFleet,
		noMoreFleetsToAdd: translations.noMoreFleetsToAdd,
		noSelectedFleets: translations.noSelectedFleets,
		fallbackTooltip: translations.fallbackTooltip,
		fallbackHeader: translations.fallbackHeader,
		priority: translations.priority,
		fast: translations.fast,
		cheap: translations.cheap,
		internalFleetName: translations.internalFleetName
	};

	const buttonTranslations: FormButtonsTranslations = {
		apply: translations.apply,
		remove: translations.remove,
		cancel: translations.cancel
	};

	const onNameChange = e => {
		setName(e.target.value);
	};

	const isPriorityRule = ruleType === RuleType.priority;

	const ruleTypeOptions = Object.values(RuleType).map(type => (
		<Select.Option key={type} value={type} label={translations[type]} className="select-rule-type-option">
			{validationByRuleType[type] && <BringgIcon iconName={BringgFontIcons.PrompedMessage} />}
			{translations[type]}
		</Select.Option>
	));

	const shouldGlow =
		prevRuleType !== RuleType.priority && !_isNil(prevRuleType) && ruleConfig.ruleType === RuleType.priority;

	return (
		<div className={classNames('rule-form', { priority: isPriorityRule })}>
			<div className="rule-form-header">{translations.editRule}</div>
			<ReadOnlyMessage showMessage={readOnly} message={translations?.readOnlyModeMessage} />
			<div className={classNames('layout', { 'priority-layout': isPriorityRule, 'glow-priority': shouldGlow })}>
				<div className="edit-name">
					<div className="edit-header">{translations.name}</div>
					<Input
						value={name}
						onChange={onNameChange}
						disabled={readOnly}
						data-test-id={TEST_IDS.RuleNameInput}
					/>
				</div>
				<div className="select-rule-type">
					<div className="edit-header">{translations.type}</div>
					<Select
						optionLabelProp="label"
						className="select-rule-type"
						value={ruleType}
						onChange={setRuleType}
						disabled={readOnly}
						data-test-id={TEST_IDS.RuleTypeInput}
						getPopupContainer={() => document.querySelector('.rule-form')}
					>
						{ruleTypeOptions}
					</Select>
					{validationByRuleType[ruleType] && (
						<div className="warning-msg-container" data-test-id={TEST_IDS.RuleTypeWarningMessage}>
							<BringgIcon iconName={BringgFontIcons.PrompedMessage} />
							<div className="warning-msg-body">{validationByRuleType[ruleType]}</div>
						</div>
					)}
				</div>
				{isPriorityRule && (
					<PriorityConfiguration
						fleets={allFleets}
						selectedFleetIds={selectedFleetIds}
						fallbackMethod={fallbackMethod}
						isFallbackMethodEnabled={isFallbackMethodEnabled}
						setFallbackMethod={setFallbackMethod}
						setIsFallbackMethodEnabled={setIsFallbackMethodEnabled}
						onChange={setSelectedFleetIds}
						translations={priorityTranslations}
						readOnly={readOnly}
					/>
				)}
				<TeamsSection
					allTeams={allTeams}
					selectedTeams={selectedTeams}
					setSelectedTeams={setSelectedTeams}
					disabledTeamIds={disabledTeamIds}
					conflictRulesByTeamId={conflictRulesByTeamId}
					isMerchantLevel={isTeamLevel}
					onTeamLevelToggle={setTeamLevel}
					translations={teamsSectionTranslations}
					readOnly={readOnly}
				/>
			</div>

			<FormButtons
				translations={buttonTranslations}
				shouldShowRemove={_isFunction(onRemove)}
				onRemove={_onRemove}
				onApply={_onApply}
				onCancel={onCancel}
				disabled={readOnly}
			/>
		</div>
	);
};

const getDefaultFleets = (allFleets: FleetsType) => {
	const ordered = _orderBy(allFleets, ['name'], ['asc']);
	return ordered.map(fleet => fleet.id);
};

export default RuleForm;
